import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TablePagination from '@mui/material/TablePagination';
import { DataLoadingCard, NoAvailableCard } from 'common';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import * as R from 'ramda';
import { hasAllRoles, hasAnyRoles, userRoles } from 'utils/currentUserRoles';
import { format, subDays } from 'date-fns';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { AuthContext } from 'authentication/AuthContext';
import { useUserRolePermissions } from 'hooks';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import usePersistentFilters, {
  dateParser,
  filtersCounter,
  makeFilterParser,
} from 'hooks/usePersistentFilters';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { useMediaQuery } from '@mui/material';
import {
  useBulkAcceptShipmentMutation,
  useCancelBatchMutation,
  useCancelShipmentMutation,
  useDeleteDistributionMutation,
  useGetBatchesQuery,
  useGetDistributionsQuery,
  useGetShipmentsQuery,
} from './batchesEndpoints';
import Header from './HeaderConnector';
import Filters from './Filters';
import BatchesCard from './BatchesCard';
import BatchesGridView from './view/BatchesGridView';
import ShipmentsCard from './ShipmentsCard';
import DistributionsCard from './DistributionsCard';
import DistributionsDrawer from './DistributionsDrawer/DistributionsDrawer';
import ShipmentsGridView from './view/ShipmentsGridView';
import DistributionsGridView from './view/DistributionsGridView';
import CreateBatchWizard from './create/CreateBatchWizard';
import {
  formatAllShipmentsFailedMessage,
  formatBulkAcceptGenericFailureMessage,
  formatPartialShipmentsFailedMessage,
} from './bulkAcceptShipmentMessages';

const panelStyle = {
  height: () => ({ lg: 'calc(100vh - 309px)' }),
  mb: { lg: 0, xs: 8 },
  minHeight: 400,
  mt: { lg: 0, xs: 16 },
  overflowY: 'auto',
  p: 0,
};

const shipmentPanelStyle = {
  ...panelStyle,
  height: { lg: 'auto', xs: 'calc(100vh - 309px)' },
  minHeight: { lg: 0, xs: 400 },
};

const Batches = () => {
  const dispatch = useDispatch();
  const { view } = useParams();
  const navigate = useNavigate();
  const isShipper = hasAllRoles([userRoles.SHIPPER]);
  const { affiliates } = useContext(AuthContext);
  const { isProductShippingManage } = useUserRolePermissions();

  const smDown = useMediaQuery('(max-width:768px)', { noSsr: true });

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [createBatch, setCreateBatch] = useState(view === 'new');
  const [copyBatch, setCopyBatch] = useState(view === 'copy');

  const initialState = {
    batchId: null,
    batchOwner: null,
    distributionId: null,
    endDate: new Date(),
    inventoryOwner: null,
    location: null,
    origin: null,
    originOwner: null,
    parentShipmentId: null,
    product: null,
    shipmentId: null,
    shipper: null,
    startDate: subDays(new Date(), 31),
    status: 'ALL',
    volume: '',
  };

  const parser = makeFilterParser({
    endDate: dateParser,
    startDate: dateParser,
  });

  const ignoreKeysForCounter = {
    batch: ['distributionId', 'inventoryOwner', 'location', 'shipmentId', 'shipper'],
    distribution: ['batchId', 'batchOwner', 'origin', 'originOwner'],
    shipment: ['distributionId', 'inventoryOwner', 'location', 'originOwner'],
  };

  const [filters, setFilters] = usePersistentFilters('batchesFilters', { ...initialState }, parser);

  const [pagination, setPagination] = useState({ page: 0, results: 100 });
  const [dialogType, setDialogType] = useState('');

  const [selectedDistribution, setSelectedDistribution] = useState();
  const [copyBatchId, setCopyBatchId] = useState(null);
  const [isBulkAcceptingShipments, setIsBulkAcceptingShipments] = useState(false);
  const [shipmentSelectionModel, setShipmentSelectionModel] = useState([]);
  const shipmentRowCacheRef = useRef(new Map());

  const handleOpenCreate = R.compose(setCreateBatch, R.T);
  const handleCloseCreate = R.compose(setCreateBatch, R.F);
  const handleCloseCopy = R.compose(setCopyBatch, R.F);
  const shipmentBulkConfirmFlag = EvaluateFlag(
    process.env.REACT_APP_LD_SHIPMENTS_BULK_CONFIRM || 'oneokSwiftShipmentsBulkConfirm'
  );
  const canUseShipmentBulkActions = shipmentBulkConfirmFlag && isProductShippingManage;
  useEffect(() => {
    if (view === 'new') {
      handleOpenCreate();
    }
  }, [handleOpenCreate, navigate, view]);

  const handleTabChange = val => {
    navigate(`../batches/${val}`);
  };

  const params = {
    batchId: R.prop('batchId', filters),
    batchOwnerId: R.path(['batchOwner', 'batchOwnerIdName'], filters),
    endDate: format(R.prop('endDate', filters), 'MM-dd-yyyy'),
    locationId: R.path(['location', 'locationId'], filters),
    originId: R.path(['origin', 'originId'], filters),
    originOwnerId: R.path(['originOwner', 'originOwnerIdName'], filters),
    pageNum: R.prop('page', pagination),
    parentBatchId: R.prop('batchId', filters),
    productId: R.path(['product', 'productId'], filters),
    results: R.prop('results', pagination),
    shipmentId: R.prop('shipmentId', filters),
    shipmentRequestId: R.prop('parentShipmentId', filters),
    shipperId: R.path(['shipper', 'shipperIdName'], filters),
    startDate: format(R.prop('startDate', filters), 'MM-dd-yyyy'),
    status: R.prop('status', filters),
    volume: R.prop('volume', filters),
  };

  const batchParams = {
    ...params,
    batchId: R.prop('batchId', filters),
    batchOwnerId: R.path(['batchOwner', 'batchOwnerIdName'], filters),
    originId: R.path(['origin', 'originId'], filters),
    originOwnerId: R.path(['originOwner', 'originOwnerIdName'], filters),
  };

  const { data: batchData = { batches: [] }, isFetching: isFetchingBatches } = useGetBatchesQuery(
    { params: batchParams },
    {
      refetchOnMountOrArgChange: true,
      skip: R.not(R.equals('batch', view)),
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCopy = (type, copyId) => {
    setDialogType(type);
    setCopyBatchId(copyId);
    setCopyBatch(R.T);
  };

  useEffect(() => {
    if (view === 'copy') {
      handleCopy();
    }
  }, [handleCopy, navigate, view]);

  const shipmentParams = {
    ...params,
    batchOwnerId: R.path(['batchOwner', 'batchOwnerIdName'], filters),
    originId: R.path(['origin', 'originId'], filters),
    parentBatchId: R.prop('batchId', filters),
    shipmentId: R.prop('shipmentId', filters),
    shipperId: R.path(['shipper', 'shipperIdName'], filters),
  };

  const {
    data: shipmentData = { shipments: [] },
    isFetching: isFetchingShipments,
    refetch: refetchShipments,
  } = useGetShipmentsQuery(
    { params: shipmentParams },
    {
      skip: R.not(R.equals('shipment', view)),
    }
  );

  useEffect(() => {
    R.propOr([], 'shipments', shipmentData).forEach(shipment => {
      shipmentRowCacheRef.current.set(String(shipment.shipmentId), shipment);
    });
  }, [shipmentData]);

  useEffect(() => {
    shipmentRowCacheRef.current.clear();
    setShipmentSelectionModel([]);
  }, [filters]);

  const handleShipmentSelectionModelChange = useCallback(
    newSelectionModel => {
      const currentPageIds = new Set(
        R.propOr([], 'shipments', shipmentData).map(shipment => String(shipment.shipmentId))
      );
      const currentPageSelection = (Array.isArray(newSelectionModel) ? newSelectionModel : [])
        .map(String)
        .filter(id => currentPageIds.has(id));

      setShipmentSelectionModel(previousSelection => {
        const otherPageSelection = previousSelection.filter(id => !currentPageIds.has(String(id)));
        return [...otherPageSelection, ...currentPageSelection];
      });
    },
    [shipmentData]
  );

  const distributionParams = {
    ...params,
    distributionId: R.prop('distributionId', filters),
    inventoryOwnerId: R.path(['inventoryOwner', 'inventoryOwnerId'], filters),
    locationId: R.path(['location', 'locationId'], filters),
    shipmentRequestId: R.prop('parentShipmentId', filters),
    shipperId: R.path(['shipper', 'shipperIdName'], filters),
    status: params.status === 'CLOSED' ? 'ALL' : params.status,
  };

  const { data: distributionData = { distributions: [] }, isFetching: isFetchingDistributions } =
    useGetDistributionsQuery(
      { params: distributionParams },
      {
        skip: R.not(R.equals('distribution', view)),
      }
    );

  // Export must send the same filters used to fetch the currently viewed tab's data.
  const exportParams = R.cond([
    [R.equals('batch'), R.always(batchParams)],
    [R.equals('shipment'), R.always(shipmentParams)],
    [R.equals('distribution'), R.always(distributionParams)],
    [R.T, R.always(params)],
  ])(view);

  const totalResults = R.cond([
    [R.equals('batch'), R.always(R.prop('totalResults', batchData))],
    [R.equals('shipment'), R.always(R.prop('totalResults', shipmentData))],
    [R.equals('distribution'), R.always(R.prop('totalResults', distributionData))],
  ])(view);

  const hasResults = R.any(R.lte(1))([
    R.prop('batches', batchData).length,
    R.prop('shipments', shipmentData).length,
    R.prop('distributions', distributionData).length,
  ]);

  // Dialog & Cancellation of Batches/Shipments/Distribution
  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);
  const [idToCancel, setIdToCancel] = useState(null);
  const [isOnlyShipment, setIsOnlyShipment] = useState(false);

  const [cancelBatch] = useCancelBatchMutation();
  const [bulkAcceptShipment] = useBulkAcceptShipmentMutation();
  const [deleteDistribution] = useDeleteDistributionMutation();
  const [cancelShipment] = useCancelShipmentMutation();

  const getBulkAcceptErrorReason = useCallback(error => {
    return (
      error?.data?.title || error?.data?.detail || error?.title || error?.detail || error?.error
    );
  }, []);

  const handleBulkAcceptShipments = useCallback(() => {
    const selectedIds = shipmentSelectionModel.map(String);
    const totalRequested = selectedIds.length;
    if (!totalRequested) return;

    dispatch(
      openConfirmationDialog({
        confirmAction: async () => {
          setIsBulkAcceptingShipments(true);
          const groupsByCompany = new Map();
          const failedIds = [];
          let firstFailureReason;

          selectedIds.forEach(id => {
            const shipment = shipmentRowCacheRef.current.get(id);
            const shipmentShipperName =
              shipment?.shipperName || R.path(['shipper', 'shipperName'], shipment);
            const affiliate = affiliates?.find(
              item => item?.shipper && item?.customerNameShort === shipmentShipperName
            );
            const dataCompanyId = affiliate?.dataCompanyId;

            if (!shipment || !dataCompanyId) {
              failedIds.push(id);
              firstFailureReason ||= 'the Shipper affiliate could not be resolved';
              return;
            }

            const normalizedDataCompanyId = Number(dataCompanyId);
            const shipmentIds = groupsByCompany.get(normalizedDataCompanyId) || [];
            shipmentIds.push(Number(shipment.shipmentId));
            groupsByCompany.set(normalizedDataCompanyId, shipmentIds);
          });

          const groups = Array.from(groupsByCompany.entries()).map(
            ([dataCompanyId, shipmentIds]) => ({ dataCompanyId, shipmentIds })
          );

          try {
            const results = await Promise.allSettled(
              groups.map(({ dataCompanyId, shipmentIds }) =>
                bulkAcceptShipment({ body: { dataCompanyId, shipmentIds } }).unwrap()
              )
            );
            let successCount = 0;

            results.forEach((result, index) => {
              const groupIds = groups[index].shipmentIds.map(String);
              if (result.status === 'fulfilled') {
                const response = result.value || {};
                successCount += Number(response.successCount) || 0;
                failedIds.push(...(response.failedShipmentIds || []).map(String));
                if (!firstFailureReason && Number(response.failureCount) > 0) {
                  firstFailureReason = getBulkAcceptErrorReason(response);
                }
              } else {
                failedIds.push(...groupIds);
                firstFailureReason ||= getBulkAcceptErrorReason(result.reason);
              }
            });

            const failureCount = totalRequested - successCount;
            const failedIdSet = new Set(failedIds);

            if (failureCount === 0) {
              dispatch(
                createSnack({
                  message: 'All selected shipments were successfully accepted.',
                  options: { autoHideDuration: 4000, variant: 'success' },
                  title: 'Action Completed',
                })
              );
              setShipmentSelectionModel([]);
            } else if (successCount === 0) {
              dispatch(
                createSnack({
                  message: formatAllShipmentsFailedMessage(firstFailureReason),
                  options: { persist: true, variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
              setShipmentSelectionModel(currentIds => currentIds);
            } else {
              dispatch(
                createSnack({
                  message: `${successCount} of ${totalRequested} shipments were accepted.`,
                  options: { autoHideDuration: 4000, variant: 'success' },
                  title: 'Action Completed',
                })
              );
              dispatch(
                createSnack({
                  message: formatPartialShipmentsFailedMessage(
                    firstFailureReason,
                    failureCount,
                    totalRequested
                  ),
                  options: { persist: true, variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
              setShipmentSelectionModel(currentIds =>
                currentIds.filter(id => failedIdSet.has(String(id)))
              );
            }
            await refetchShipments();
          } catch (error) {
            dispatch(
              createSnack({
                message: formatBulkAcceptGenericFailureMessage(getBulkAcceptErrorReason(error)),
                options: { persist: true, variant: 'error' },
                title: 'Action Not Completed',
              })
            );
          } finally {
            setIsBulkAcceptingShipments(false);
          }
        },
        header: `Accept ${totalRequested} Shipments`,
        message: 'Are you sure you want to accept the selected shipments?',
      })
    );
  }, [
    affiliates,
    bulkAcceptShipment,
    dispatch,
    getBulkAcceptErrorReason,
    refetchShipments,
    shipmentSelectionModel,
  ]);

  const cancelSelectedBatch = () => {
    cancelBatch({ body: { batchId: idToCancel } })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          setIsExplicitDialogOpen(false);
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToCancel(null);
          setDialogType('');
        }, 500)
      );
  };

  const deleteSelectedDistribution = () => {
    deleteDistribution({ body: { distributionId: idToCancel } })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          setIsExplicitDialogOpen(false);
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToCancel(null);
          setDialogType('');
          setIsExplicitDialogOpen(false);
        }, 500)
      );
  };

  const cancelSelectedShipment = () => {
    cancelShipment({ body: { shipmentId: idToCancel } })
      .then(res => {
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToCancel(null);
          setDialogType('');
          setIsOnlyShipment(false);
        }, 500)
      );
  };

  const handleConfirm = () => {
    if (R.equals('batch', dialogType)) cancelSelectedBatch();
    if (R.equals('distribution', dialogType)) deleteSelectedDistribution();
    if (R.equals('shipment', dialogType)) cancelSelectedShipment();
  };

  const dialogAction = () => {
    return dialogType === 'distribution' ? 'DELETE' : 'CANCEL';
  };

  const handleCancel = (type, cancelId) => {
    setDialogType(type);
    setIdToCancel(cancelId);
    setIsExplicitDialogOpen(true);
  };

  const handleShipmentCancel = (type, cancelId, isTheOnlyShipment) => {
    handleCancel(type, cancelId);
    setIsOnlyShipment(isTheOnlyShipment);
  };

  const warningList = R.cond([
    [
      R.equals('batch'),
      R.always([
        'This process will also cancel all associated shipments.',
        'This action cannot be undone.',
      ]),
    ],
    [R.equals('distribution'), R.always(['This action cannot be undone.'])],
    [
      R.equals('shipment'),
      R.always([
        'This will also cancel all associated distributions.',
        'This action cannot be undone.',
      ]),
    ],
    [R.T, R.always([])],
  ])(dialogType);

  // Add additional warnings if cancelling the last/only shipment
  const additionalWarnings = [
    'You are removing the last shipment in this batch.',
    'This will make the Batch invalid until at least one shipment is added.',
  ];

  const renderSkeleton = R.always(<DataLoadingCard type="data" />);

  return (
    <Box sx={{ pb: 0, pt: { lg: 9, xs: 7 }, px: { lg: 3, xs: 2 } }}>
      <Header
        exportParams={exportParams}
        filterCount={filtersCounter(initialState, filters, ignoreKeysForCounter[view], parser)}
        onCloseCreate={handleCloseCreate}
        onOpenCreate={handleOpenCreate}
        setIsFiltersOpen={setIsFiltersOpen}
      />
      <TabContext value={view === 'new' ? 'batch' : view}>
        <Box
          sx={{
            backgroundColor: 'surface.light',
            borderBottom: 1,
            borderColor: 'divider',
            bottom: 0,
            boxShadow: { lg: 'none', xs: 1 },
            left: 0,
            mb: { lg: 0, xs: 0 },
            mt: { lg: 2, xs: 1 },
            position: { lg: 'relative', xs: 'fixed' },
            width: 1,
            zIndex: 1,
          }}
        >
          <TabList
            onChange={R.compose(handleTabChange, R.nthArg(1))}
            sx={{
              '& > div > .MuiTabs-flexContainer': {
                justifyContent: { lg: 'flex-start', xs: 'center' },
              },
            }}
          >
            <Tab label="Batches" value="batch" />
            <Tab label="Shipments" value="shipment" />
            <Tab label="Distributions" value="distribution" />
          </TabList>
        </Box>

        {isShipper && (
          <>
            <TabPanel sx={panelStyle} value="batch">
              {isFetchingBatches ? (
                renderSkeleton()
              ) : !R.prop('batches', batchData).length ? (
                <NoAvailableCard type="batches" />
              ) : smDown ? (
                <Box sx={{ pt: 4 }}>
                  {R.prop('batches', batchData).map(batch => (
                    <BatchesCard
                      key={R.prop('batchId', batch)}
                      {...batch}
                      onCancel={handleCancel}
                      onCopy={handleCopy}
                      params={params}
                    />
                  ))}
                </Box>
              ) : (
                <BatchesGridView
                  batches={R.prop('batches', batchData)}
                  onCancel={handleCancel}
                  onCopy={handleCopy}
                />
              )}
            </TabPanel>
            <TabPanel sx={shipmentPanelStyle} value="shipment">
              {smDown ? (
                isFetchingShipments ? (
                  renderSkeleton()
                ) : !R.prop('shipments', shipmentData).length ? (
                  <NoAvailableCard type="shipments" />
                ) : (
                  <Box sx={{ pt: 4 }}>
                    {R.prop('shipments', shipmentData).map(shipment => (
                      <ShipmentsCard
                        fromPage="Overview"
                        isParentBatchView={false}
                        key={R.prop('shipmentId', shipment)}
                        {...shipment}
                        onCancel={handleShipmentCancel}
                      />
                    ))}
                  </Box>
                )
              ) : (
                <ShipmentsGridView
                  canUseBulkActions={canUseShipmentBulkActions}
                  isProcessing={isBulkAcceptingShipments}
                  loading={isFetchingShipments}
                  onAcceptShipments={handleBulkAcceptShipments}
                  onCancel={handleShipmentCancel}
                  onPaginationChange={model =>
                    setPagination({ page: model.page, results: model.pageSize })
                  }
                  onResetSelection={() => setShipmentSelectionModel([])}
                  onSelectionModelChange={handleShipmentSelectionModelChange}
                  paginationModel={{
                    page: R.prop('page', pagination),
                    pageSize: R.prop('results', pagination),
                  }}
                  rowSelectionModel={shipmentSelectionModel}
                  shipments={R.prop('shipments', shipmentData)}
                  totalResults={R.defaultTo(0, R.prop('totalResults', shipmentData))}
                />
              )}
            </TabPanel>
            {hasAnyRoles([userRoles.SHIPPER, userRoles.INVENTORY_OWNER]) && (
              <TabPanel sx={panelStyle} value="distribution">
                {isFetchingDistributions ? (
                  renderSkeleton()
                ) : !R.prop('distributions', distributionData).length ? (
                  <NoAvailableCard type="distributions" />
                ) : (
                  <>
                    <DistributionsDrawer
                      onClose={setSelectedDistribution}
                      setSelectedDistribution={setSelectedDistribution}
                      {...selectedDistribution}
                    />
                    {smDown ? (
                      <Box sx={{ pt: 4 }}>
                        {R.prop('distributions', distributionData).map(distribution => (
                          <DistributionsCard
                            fromPage="Batches Overview"
                            key={R.prop('distributionId', distribution)}
                            onDelete={handleCancel}
                            setSelectedDistribution={mode =>
                              setSelectedDistribution({ ...distribution, ...mode })
                            }
                            {...distribution}
                          />
                        ))}
                      </Box>
                    ) : (
                      <DistributionsGridView
                        distributions={R.prop('distributions', distributionData)}
                        onDelete={handleCancel}
                        setSelectedDistribution={setSelectedDistribution}
                      />
                    )}
                  </>
                )}
              </TabPanel>
            )}
          </>
        )}

        {hasResults && view !== 'shipment' && (
          <TablePagination
            count={R.defaultTo(0, totalResults)}
            onPageChange={(_, newPage) => {
              setPagination(R.assoc('page', newPage, pagination));
              window.scrollTo(0, 0);
            }}
            onRowsPerPageChange={event => {
              setPagination(
                R.evolve(
                  {
                    page: R.always(0),
                    results: R.always(event.target.value),
                  },
                  pagination
                )
              );
              window.scrollTo(0, 0);
            }}
            page={R.prop('page', pagination)}
            rowsPerPage={R.prop('results', pagination)}
            rowsPerPageOptions={[50, 100, 150, 200]}
            sx={{
              '.MuiTablePagination-selectLabel': {
                display: { lg: 'block', xs: 'none' },
              },
              bottom: { lg: 0, xs: 48 },
            }}
          />
        )}
      </TabContext>
      <Filters
        filters={filters}
        initialState={initialState}
        isFiltersOpen={isFiltersOpen}
        pagination={pagination}
        setFilters={setFilters}
        setIsFiltersOpen={setIsFiltersOpen}
        setPagination={setPagination}
      />
      <DialogExplicitConfirmation
        confirmationMessage={`If you still want to ${dialogAction()} the ${dialogType}, enter the word below and click Confirm.`}
        confirmationValue={dialogAction()}
        dialogOpen={isExplicitDialogOpen}
        handleCloseDialog={() => {
          setIsExplicitDialogOpen(false);
          setTimeout(() => {
            setDialogType('');
            setIdToCancel(null);
          }, 600);
        }}
        label={`Type "${dialogAction()}"`}
        onConfirm={handleConfirm}
        title={`Are you sure you want to ${dialogAction()} this ${dialogType}?`}
        warningList={!isOnlyShipment ? warningList : R.concat(additionalWarnings, warningList)}
      />
      {createBatch && (
        <CreateBatchWizard
          onClose={() => {
            handleCloseCreate();
            navigate('../batches/batch');
          }}
        />
      )}

      {copyBatch && (
        <CreateBatchWizard
          copyBatchId={copyBatchId}
          onClose={() => {
            handleCloseCopy();
            setCopyBatchId(null);
          }}
        />
      )}
    </Box>
  );
};

export default Batches;
